import {
  Platform,
  View,
  StyleSheet,
  Text,
  useWindowDimensions,
  Linking,
  ViewStyle,
  StyleProp,
} from "react-native";
import WebView from "react-native-webview";
import RText from "../RText";
import { RLink } from "../RLink/RLink";
import variables from "../../styles/variables";
import React, { ReactChild, useEffect, useState } from "react";
import RHeading from "../RHeading/RHeading";
import { RAttachment } from "../RAttachment/RAttachment";
import { TwitterTweetEmbed } from "react-twitter-embed";
import { extractTweetId } from "./utils";
import { useResponsive } from "../../hooks/useResponsive";
import WebIframe from "../WebIframe";
import { useAtom } from "jotai";
import { globalFullscreenAtom, scriptsAtom } from "../../atoms";

import AdBanner from "../Layout/AdBanners/AdBanner";
import useDeviceType, { DeviceTypeLabels } from "../../hooks/useDeviceType";
import { Tweet as RNTweet } from "rn-tweet-embed";
import findInReactTree from "../../utils/findInReactTree";
import RYoutubePlayer from "../RYoutubePlayer";

const dayjs = require("dayjs");

export const RCaption = ({ children, type, id, caption }) => (
  <View style={styles.imageCaption}>
    <RText style={styles.imageCaption} weight="light" color="grey">
      {caption}
    </RText>
  </View>
);

export const Space = ({ children }) => <View style={styles.space}></View>;

export const RBold = ({ children, color }) => {
  const { isMediumAndDown } = useResponsive();

  return (
    <RText color={color} weight="bold" size={isMediumAndDown ? "sm" : "md"}>
      {children}
    </RText>
  );
};

export const Bold = ({ children, color }) => {
  const { isMediumAndDown } = useResponsive();
  return (
    <RText color={color} weight="bold" size={isMediumAndDown ? "sm" : "md"}>
      {children}
    </RText>
  );
};

export const RItalic = ({ children, color }) => {
  const { isMediumAndDown } = useResponsive();

  return (
    <RText
      color={color}
      style={styles.italic}
      size={isMediumAndDown ? "sm" : "md"}
    >
      {children}
    </RText>
  );
};

export const RStrikeThrough = ({ children, color }) => {
  const { isMediumAndDown } = useResponsive();

  return (
    <RText
      color={color}
      style={styles.strikethrough}
      size={isMediumAndDown ? "sm" : "md"}
    >
      {children}
    </RText>
  );
};

export const RParagraph = ({ children, color, style, textSize }) => {
  const { isMediumAndDown, isSmallAndUp } = useResponsive();
  const [isGlobalFullScreen] = useAtom(globalFullscreenAtom);

  if (isGlobalFullScreen) return null;

  return (
    <RText
      size={textSize ? textSize : isMediumAndDown ? "sm" : "md"}
      style={[
        styles.paragraph,
        style,

        // remove all margin bottom on tablets
        isSmallAndUp && Platform.OS != "web" ? { marginBottom: 0 } : {},
      ]}
      color={color}
    >
      {children}
    </RText>
  );
};

export const RListOrdered = ({ children, color }) => (
  <RList type="ol" color={color}>
    {children}
  </RList>
);

export const RListUnordered = ({ children, color }) => (
  <RList type="ul" color={color}>
    {children}
  </RList>
);

export const RHorizontalRule = () => <View style={styles.hr} />;

export const RList = ({ children, type, color }) => {
  if (Platform.OS == "android") return; // until we solve formatting issue on android
  const wrappedChildren = children.length ? children : [children];
  return (
    <View style={styles.list}>
      {wrappedChildren.map((child, index) => {
        return (
          <View style={styles.item} key={`wrappedChildren${index}`}>
            {type === "ol" ? (
              <NumberedBullet color={color}>{index + 1}.</NumberedBullet>
            ) : (
              <Bullet color={color} />
            )}
            {/* another RList or a RListItem */}
            <View style={styles.childWrap}>{child}</View>
          </View>
        );
      })}
    </View>
  );
};

export const NumberedBullet = ({ children, color }) => {
  const { isMediumAndDown } = useResponsive();
  return (
    <RText
      color={color}
      style={styles.normalBulletText}
      size={isMediumAndDown ? "sm" : "md"}
    >
      {children}
    </RText>
  );
};

export const Bullet = ({ color }) => {
  const { isMediumAndDown } = useResponsive();

  return (
    <RText
      color={color}
      style={styles.normalBulletText}
      size={isMediumAndDown ? "sm" : "md"}
    >
      •
    </RText>
  );
};

export const RListItem = ({ children, color }) => {
  const { isMediumAndDown } = useResponsive();
  const wrappedChildren = children.length ? children : [children];
  return wrappedChildren.map((child, index) => {
    if (child?.props?.href) {
      return <React.Fragment key={child.props.href}>{child}</React.Fragment>;
    } else {
      return (
        <RText
          color={color}
          key={`list-item-${index}`}
          size={isMediumAndDown ? "sm" : "md"}
        >
          {child}
        </RText>
      );
    }
  });
};

export const RBlockQuote = ({ children }) => {
  const { isMediumAndDown } = useResponsive();

  return (
    <RText style={styles.blockquote} size={isMediumAndDown ? "sm" : "md"}>
      {children}
    </RText>
  );
};

export const RDate = ({ date }: { date: string }) => (
  <RText
    size="md"
    color="grey"
    uppercase
    weight="bold"
    style={{ marginBottom: 10, display: "flex" }}
  >
    {dayjs(date).format("ddd DD MMM YYYY")}
  </RText>
);

export const Iframe = ({
  src,
  width,
  height,
  type = undefined,
  style = undefined,
}) => {
  if (!height) height = style?.height || "300px";
  if (!width) width = style?.width || "100%";

  const isWeb = Platform.OS === "web";

  const { isSmallAndDown, isSmallAndUp } = useResponsive();
  const [webViewHeight, setWebViewHeight] = useState(300);

  if (src.includes("youtube") && !src.includes("sociablekit")) {
    // app, phones
    if (Platform.OS === "android" && isSmallAndDown) {
      width = "336px";
      height = 189;
    }

    if (Platform.OS === "ios" && isSmallAndDown) {
      width = "408px";
      height = 230;
    }

    // tablets
    if (!isWeb && isSmallAndUp) {
      width = "853px";
      height = 505;
    }

    // handle overflow if width is greater than screen width
    const { width: screenWidth } = useWindowDimensions();
    width = width > screenWidth ? "100%" : width;

    if (Platform.OS === "web") {
      // put px on height if it's just a number (to prevent iframe breaking on web)
      if (Number(height)) height = `${height}px`;
    } else {
      // Assumes src starts with https://www.youtube.com/embed/
      let videoId = src.split("/").pop().split("?")[0];
      return <RYoutubePlayer height={height} videoId={videoId} />;
    }
  }

  let source = src;

  // if source includes opening and closing a tags, remove them and just use href
  if (source.includes("<a")) {
    source = source.replace(/<a[^>]*>/g, "");
    source = source.replace(/<\/a>/g, "");
  }

  let heightNumber = height.includes("%")
    ? height
    : parseInt(height.replace("px", ""));
  let widthNumber = width.includes("%")
    ? width
    : parseInt(width.replace("px", ""));
  if (Platform.OS === "android" && src.includes("wufoo"))
    heightNumber = heightNumber + 30;

  if (Platform.OS === "web") {
    if (type == "ad") {
      return <WebIframe width={width} height={height} src={src} />;
    } else {
      return (
        <iframe
          src={src}
          width={width}
          height={height}
          frameBorder="0"
        ></iframe>
      );
    }
  } else {
    const originalUrl = src;

    const injectedJavaScript = `
  (function() {
    function sendHeight() {
      const height = Math.max(
        document.body.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.scrollHeight,
        document.documentElement.offsetHeight
      );
      window.ReactNativeWebView.postMessage(
        JSON.stringify({ type: 'webview-height', height: height })
      );
    }

    sendHeight();
    const observer = new MutationObserver(sendHeight);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });
  })();
  true;
`;
    return (
      <WebView
        injectedJavaScript={injectedJavaScript}
        allowsFullscreenVideo={true}
        javaScriptEnabled={true}
        onMessage={(event) => {
          try {
            const data = JSON.parse(event.nativeEvent.data);
            if (data.type === "webview-height") setWebViewHeight(data.height);
          } catch (_) {}
        }}
        source={{ uri: source }}
        originWhitelist={["*"]}
        // scrollEnabled={false} // There's no easy way
        // of knowing at this point whether we're in an ad or something
        // live a wufoo form, so we need to enable scroll.
        style={{
          height: webViewHeight,
          width: !isWeb ? undefined : widthNumber || "100%",
          backgroundColor: "transparent",
          marginTop: -4, // brings ad up a touch
          marginLeft: "-4%", // TOM-COMBACK TODO - this 4% is good for iframe banner adds being centered, but bad for youtube videos (on android - could be other envs)
          opacity: 0.99, // yeah really. https://github.com/react-native-webview/react-native-webview/issues/811#issuecomment-614152848
        }} //styles.iFrameMobile}
        onShouldStartLoadWithRequest={(request) => {
          // open external link in browser
          //this only seems to run on ios
          //runs on mount and on every link click

          if (Platform.OS === "ios") {
            const firstLoad =
              decodeURI(request.mainDocumentURL).trim() ===
                decodeURI(originalUrl).trim() && !request.canGoBack;
            if (!request.isTopFrame || firstLoad) return true;
          }

          if (request.mainDocumentURL !== originalUrl) {
            Linking.canOpenURL(request.url).then(
              (supported) => supported && Linking.openURL(request.url)
            );

            return false;
          } else return true;
        }}
      />
    );
  }
};

const RTweet = ({ href }) => {
  const { isSmallAndDown } = useResponsive();

  const match = extractTweetId(href);

  try {
    if (match)
      if (Platform.OS === "web")
        return (
          <View
            style={{
              alignItems: !isSmallAndDown ? "center" : undefined,
            }}
          >
            <TwitterTweetEmbed tweetId={match} />
          </View>
        );
      else return <RNTweet theme="light" tweetId={match} />;
  } catch {
    return <></>;
  }
};

const createHTMLTag = (tag: string, child: any) => {
  const { isSmallAndDown } = useResponsive();

  const copy = { ...child.props };
  const iterable = Object.entries(copy);
  const isWeb = Platform.OS === "web";

  const propsAsString = iterable
    .map(([key, val]) => {
      if (key === "children") return;
      if (key === "className") key = "class";
      if (key === "style") {
        const style = Object.entries(val).map(([k, v]) => {
          if (k === "backgroundColor") return `background-color:${v}`;
          if (k === "height" && isSmallAndDown && copy?.mobileHeight && isWeb) {
            return `height:${copy?.mobileHeight}px`;
          }
          if (k === "width" && isSmallAndDown && copy?.mobileWidth && isWeb) {
            return `width:${copy?.mobileWidth}px`;
          }

          return `${k}:${v}`;
        });

        return `style="${style.join(";")}"`;
      }

      return `${key}="${val}"`;
    })
    .join(" ")
    .replace(/,/g, " ");

  return `<${tag} ${propsAsString}></${tag}>`;
};

const createScript = (script: string, index: number) => {
  const splitScript = script.split(" ");
  const source = splitScript
    .find((s) => s.startsWith("src="))
    .replace("src=", "");
  const s = document.createElement("script");
  s.src = `${source}`.replace(/&amp;/g, "&");
  splitScript.includes("async") && (s.async = true);
  splitScript.includes("defer") && (s.defer = true);
  s.id = `script${index}`;
  return s;
};

const returnScripts = () => {
  const [scripts] = useAtom(scriptsAtom);
  return `${scripts
    .map((script, index) => {
      const isDotDigitalScript = script.includes("dotdigital");

      if (Platform.OS === "web") {
        const alreadyExists = !!document.getElementById(`script${index}`);
        if (alreadyExists) return;
        const s = createScript(script, index);
        if (isDotDigitalScript) {
          const dotDigitalWrapper =
            document.getElementById("dotDigitalWrapper");
          if (dotDigitalWrapper) dotDigitalWrapper.appendChild(s);
        } else {
          document.body.appendChild(s);
        }
      }

      return `${script}`;
    })
    .join(" ")}`;
};

const createHTMLString = (wrapper: string, child: any) => {
  return `<!DOCTYPE html>
    <html>
    <head>
    </head>
    <body>
    ${createHTMLTag(wrapper, child)}
    ${returnScripts()}
    </body>
    </html>`;
};

const extractMobileDimensions = (htmlString: string) => {
  if (typeof htmlString !== "string") return;
  const mobileHeight = htmlString
    .split(" ")
    .find((s: string) => s.startsWith(`mobileHeight`));

  const mobileWidth = htmlString
    .split(" ")
    .find((s: string) => s.startsWith(`mobileWidth`));

  const height = parseInt(mobileHeight?.split("=")[1].replace(/"/g, ""));
  const width = parseInt(mobileWidth?.split("=")[1].replace(/"/g, ""));

  return {
    height,
    width,
  };
};

const Pre = ({ children }) => {
  const { isSmallAndDown } = useResponsive();

  //preformatted text strings and tweets
  if (children?.type === "pre") {
    let child = children?.props?.children || children[0]?.props?.children;

    if (Array.isArray(child)) child = child[0];

    //handle tweets
    const tweetUrl = findInReactTree<string>(
      children,
      (value): value is string =>
        typeof value === "string" &&
        /^https?:\/\/(www\.)?(twitter\.com|x\.com)\/[^/]+\/status\/\d+/.test(
          value
        )
    );

    if (child?.type === "blockquote") return <RTweet href={tweetUrl} />;

    if (child?.type === "div") {
      if (Platform.OS === "web") {
        returnScripts();
        return <div {...child.props} />;
      } else {
        const htmlString = createHTMLString("div", child);

        const { height, width } = extractMobileDimensions(htmlString);
        return (
          <WebView
            originWhitelist={["*"]}
            allowsFullscreenVideo={true}
            javaScriptEnabled={true}
            source={{
              html: htmlString,
            }}
            style={{ height: height || 300, width: width || "100%" }}
            // injectedJavaScript={jsCode}
            onError={(e) => console.log("error", e)}
            onMessage={(e) => console.log("message", e)}
          />
        );
      }
    }

    // handle ins
    if (child?.type === "ins") {
      const htmlString = createHTMLString("ins", child);
      const isWeb = Platform.OS === "web";
      const { height, width } = extractMobileDimensions(htmlString);
      if (isWeb) {
        return (
          <AdBanner
            heightOffset={20}
            ad={{
              iframe: `${createHTMLTag("ins", child)}
              ${returnScripts()}
              `,
            }}
          />
        );
      } else {
        return (
          <WebView
            originWhitelist={["*"]}
            allowsFullscreenVideo={true}
            javaScriptEnabled={true}
            source={{ html: htmlString }}
            containerStyle={{
              height: height || 200,
              width: width || "100%",
            }}
          />
        );
      }
    }

    //handle widgets
    if (child?.props && child?.props?.src) {
      const mobileHeight = isSmallAndDown && child?.props?.mobileHeight;
      const mobileWidth = isSmallAndDown && child?.props?.mobileWidth;

      return (
        <Iframe
          src={child?.props?.src}
          width={
            mobileWidth ||
            child?.props?.width ||
            child?.props?.style?.width ||
            "100%"
          } // default to 100% if no width is provided
          height={
            mobileHeight ||
            child?.props.height ||
            child?.props.style?.width ||
            300
          } // default to 300 if no height is provided
        />
      );
    }

    //handle preformatted text strings
    const text = child?.props?.children;

    if (typeof text !== "string") return null;

    return <Text>{text}</Text>;
  }

  // else return an empty view (to avoid a type bug on mobile when we return <>{children}</> which shouldn't ever be needed)
  return <View />;
};

export const RHtml = ({ children }) => {
  //handle pres

  if (children?.type === "pre" || children[0]?.type === "pre") {
    return <Pre>{children}</Pre>;
  }

  return <>{children}</>;
};

export const RScript = ({ src, location }) => {
  const { deviceType } = useDeviceType();

  if (deviceType === DeviceTypeLabels.UNKNOWN) return null; // wait for device type to be determined

  const isPhone = deviceType === DeviceTypeLabels.PHONE;
  const isDesktop = deviceType === DeviceTypeLabels.DESKTOP;

  let height, width;
  switch (location) {
    case "header":
      height = isPhone ? 60 : 90;
      width = isPhone ? 315 : 728;
      break;
    case "skyscraper":
      height = 600;
      width = 250;
      break;
    case "tab":
      height = 60;
      width = 315;
      break;
    default:
      height = 90;
      width = 728;
      break;
  }

  if (Platform.OS === "web") {
    return (
      <WebIframe
        height={height}
        width={width}
        srcDoc={`
        <html>
  <head>
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
  </head>
  <body style="margin:0; padding:0; overflow:hidden; ${
    isDesktop ? "display:flex;align-items:center; justify-content:center;" : ""
  }">
    <script>
      setTimeout(() => {
        const script = document.createElement('script');
        script.src = "${src}";
        script.async = true;
        document.body.appendChild(script);
      }, 500);
    </script>
  </body>
</html>

       `}
      />
    );
  }

  return (
    <View style={{ minHeight: height, maxHeight: height, width: "100%" }}>
      <WebView
        style={{ height: "100%", width: "100%" }}
        overScrollMode="never" // ⬅️ (Android) no “bounce”/overscroll
        // iOS: needed to support window.open creating a new "window"
        onOpenWindow={(event) => {
          const url = event.nativeEvent.targetUrl;
          // same policy as above
          Linking.openURL(url);
        }}
        source={{
          html: `
             <html>
              <head>
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <style>
                  html, body {
                    margin: 0;
                    padding: 0;
                    overflow: hidden;       /* ⬅️ no scrollbars ever */
                  }
                  /* (Optional) Hide WebKit scrollbars completely */
                  ::-webkit-scrollbar {
                    display: none;
                  }
                </style>
              </head>
               <body>
                 <div id="ad-container" style="display:flex; visibility:hidden; justify-content:center; align-items:center;">
                   <script src="${src}"
                      onload="setTimeout(() => {
                          document.getElementById('ad-container').style.visibility = 'visible';
                        }, 400);"
                   ></script>
                 </div>
               </body>
             </html>
            `,
        }}
      />
    </View>
  );
};

export const SUPPORTED_COMPONENTS = (textSize) => {
  return {
    Iframe,
    RHtml,
    RCaption,
    RText,
    RDate,
    RAttachment,
    View,
    RBold,
    Bold,
    RItalic,
    RStrikeThrough,
    RLink,
    Space,
    RParagraph: (props) => <RParagraph {...props} textSize={textSize} />,
    RBlockQuote,
    RHeading,
    RHorizontalRule,
    RListOrdered,
    RListUnordered,
    RListItem,
    RScript,
  };
};

export const COLOURED_COMPONENTS = (color, textSize) => {
  return {
    Iframe,
    RHtml,
    RCaption,
    RText,
    RDate,
    RAttachment,
    View,
    Space,
    RBold: (props) => <RBold {...props} color={color} />,
    Bold,
    RItalic: (props) => <RItalic {...props} color={color} />,
    RStrikeThrough: (props) => <RStrikeThrough {...props} color={color} />,
    RLink: (props) => <RLink {...props} color={color} />,
    RParagraph: (props) => (
      <RParagraph {...props} color={color} textSize={textSize} />
    ),
    RBlockQuote,
    RHeading: (props) => <RHeading {...props} color={color} />,
    RHorizontalRule,
    RListOrdered: (props) => <RListOrdered {...props} color={color} />,
    RListUnordered: (props) => <RListUnordered {...props} color={color} />,
    RListItem: (props) => <RListItem {...props} color={color} />,
    RScript,
  };
};

const { spacing, font, colors } = variables;

export const styles = StyleSheet.create({
  italic: {
    fontStyle: "italic",
  },
  strikethrough: {
    textDecorationLine: "line-through",
  },
  blockquote: {
    marginLeft: spacing.xxsmall,
    borderStyle: "solid",
    borderLeftWidth: 5,
    borderLeftColor: colors.palette.rtv.secondary,
    paddingLeft: spacing.xsmall,
    marginBottom: spacing.xsmall,
  },
  normalText: {
    fontSize: font.size.lg,
  },
  heading: {
    marginBottom: spacing.xsmall,
  },
  h1: {
    fontSize: font.size.xl,
  },
  h2: {
    fontSize: font.size.lg,
  },
  h3: {
    fontSize: 18,
  },
  h4: {
    fontSize: font.size.md,
  },
  h5: {
    fontSize: font.size.sm,
  },
  h6: {
    fontSize: font.size.xs,
  },
  paragraph: {
    marginBottom: spacing.xsmall,
  },
  list: {
    maxWidth: "100%",
    width: "100%",
  },
  item: {
    flexDirection: "row",
    marginTop: spacing.xxxsmall,
    marginLeft: spacing.xxsmall,
  },
  childWrap: {
    width: "100%",
  },
  normalBulletText: {
    paddingRight: spacing.xxsmall,
  },
  hr: {
    borderBottomColor: colors.palette.neutral.x50,
    borderBottomWidth: 1,
    // marginBottom: spacing.xxsmall, // DO NOT UNCOMMENT THIS LINE
    width: "100%",
  },
  imageCaption: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    marginBottom: spacing.xxxsmall,
    marginTop: -spacing.xxxsmall,
  },
  space: {
    minHeight: 2, // there is already a margin bottom applied of 10
    maxHeight: 2,
  },
});
